/**
 * Preview Mode Utilities
 *
 * Allows template developers to test different queue states
 * without connecting to the real CrowdHandler API.
 *
 * Usage: Set "dev": true in the <script id="crowdhandler-data"> JSON object.
 */

import { CrowdHandlerWRState, CountdownState } from '../types/crowdhandler';
import { chDefaultQueueState } from '../config/defaults';

export interface PreviewConfig {
    // Queue state (determines which view is shown)
    status: 1 | 2 | 3 | 4 | 5 | 100;  // 1=active, 2=error, 3=blocked, 4=countdown, 5=roomFull, 100=captcha

    // Display data
    title?: string;
    message?: string;
    logo?: string;

    // Queue position data
    position?: number;
    estimate?: number;
    progress?: number;
    rate?: number;

    // Stock display
    stock?: number | null;

    // Countdown (for status 4)
    onsale?: string;  // ISO date string

    // Feature availability
    emailAvailable?: number | boolean;
    priorityAvailable?: number | boolean;

    // Captcha simulation
    captchaRequired?: boolean;
    captchaType?: string;
    captchaKeyPublic?: string;

    // Session data
    token?: string;
    slug?: string;

    // Session timeout settings
    sessionsExpire?: number;
    sessionsTimeout?: number;

    // Simulate polling (position decreases over time)
    simulatePolling?: boolean;
    pollInterval?: number;  // ms, default 5000 for preview
    positionDecrement?: number;  // how much position decreases per poll
}

/**
 * Default preview configuration showing an active queue
 */
export const defaultPreviewConfig: PreviewConfig = {
    status: 1,
    title: 'Preview Event',
    message: 'This is a preview of the waiting room',
    position: 150,
    estimate: 12,
    progress: 25,
    rate: 10,
    stock: null,
    emailAvailable: 0,
    priorityAvailable: 0,
    token: 'tok0123456789',
    slug: 'preview-room',
    sessionsExpire: 0,
    sessionsTimeout: 10,
    simulatePolling: true,
    pollInterval: 5000,
    positionDecrement: 5,
};

/**
 * Preset configurations for common test scenarios
 */
export const previewPresets = {
    // Active queue with position
    active: {
        status: 1 as const,
        position: 150,
        estimate: 12,
        progress: 25,
    },

    // Active queue, nearly at front
    almostThere: {
        status: 1 as const,
        position: 3,
        estimate: 0,
        progress: 98,
    },

    // Countdown before queue opens
    countdown: {
        status: 4 as const,
        onsale: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
    },

    // Blocked user
    blocked: {
        status: 3 as const,
    },

    // Room at capacity
    roomFull: {
        status: 5 as const,
    },

    // Captcha required
    captcha: {
        status: 1 as const,
        captchaRequired: true,
        captchaType: 'recaptcha',
    },

    // Low stock warning
    lowStock: {
        status: 1 as const,
        position: 50,
        estimate: 5,
        stock: 8,
    },

    // Sold out
    soldOut: {
        status: 1 as const,
        position: 50,
        estimate: 5,
        stock: 0,
    },

    // Long wait
    longWait: {
        status: 1 as const,
        position: 5000,
        estimate: 120,
        progress: 5,
    },
};

/**
 * Merges preview config with defaults
 */
export function mergePreviewConfig(config: Partial<PreviewConfig>): PreviewConfig {
    return {
        ...defaultPreviewConfig,
        ...config,
    };
}

/**
 * Converts preview config to CrowdHandlerWRState format
 */
export function previewConfigToState(config: PreviewConfig): Partial<CrowdHandlerWRState> {
    const state: Partial<CrowdHandlerWRState> = {
        status: config.status,
        title: config.title || '',
        message: config.message || '',
        logo: config.logo || '',
        position: config.position || null,
        estimate: config.estimate || null,
        progress: config.progress || 10,
        rate: config.rate || 0,
        stock: config.stock !== undefined ? config.stock : null,
        onsale: config.onsale || '',
        emailAvailable: typeof config.emailAvailable === 'boolean'
            ? (config.emailAvailable ? 1 : 0)
            : (config.emailAvailable || 0),
        slug: config.slug || 'preview',
        sessionsExpire: config.sessionsExpire || 0,
        sessionsTimeout: config.sessionsTimeout || 10,
        captchaRequired: config.captchaRequired || false,
        token: {
            value: config.token || 'preview_token',
            obfuscated: config.token ? `${config.token.slice(0, 4)}...${config.token.slice(-4)}` : 'prev...oken',
            copied: false,
            copy: () => { },
        },
        priority: {
            available: typeof config.priorityAvailable === 'boolean'
                ? (config.priorityAvailable ? 1 : 0)
                : (config.priorityAvailable || 0),
            code: null,
            valid: false,
            submitting: false,
            submitted: false,
            applied: null,
            error: false,
            success: false,
        },
    };

    return state;
}

/**
 * Creates a simulated poll response for preview mode.
 * Returns progress + estimate only — position is handled by applyPoll.
 */
export function simulatePollResponse(
    currentState: CrowdHandlerWRState,
    config: PreviewConfig
): Partial<CrowdHandlerWRState> {
    const decrement = config.positionDecrement || 5;
    const currentPosition = currentState.position || config.position || 150;
    // Where position will be after this cycle (used for progress/estimate calc)
    const projectedPosition = Math.max(1, currentPosition - decrement);

    // Progress increases relative to starting config position
    const startingPosition = config.position || 150;
    const progressFromPosition = ((startingPosition - projectedPosition) / startingPosition) * 100;
    const newProgress = Math.min(99, Math.max(currentState.progress || 10, Math.round(progressFromPosition)));

    // Scale estimate proportionally from the configured starting values
    const initialEstimate = config.estimate || 12;
    const ratio = projectedPosition / startingPosition;
    const newEstimate = Math.max(1, Math.ceil(initialEstimate * ratio));

    return {
        progress: newProgress,
        estimate: newEstimate,
        requested: new Date().toISOString(),
    };
}

